import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { isKitheDomain } from "@/lib/kithe-domain";
import { OPEN_SETTINGS_EVENT, getConsent, setConsent } from "@/lib/consent";

const PRIVACY_URL = "https://rigorous-freeze-883.notion.site/Privacy-Policy-ERNEST-398bb534f5888021bfb9faad96b4f8b0?source=copy_link";
const PRIVACY_URL_FR = "https://rigorous-freeze-883.notion.site/Politique-de-Confidentialit-ERNEST-398bb534f588807d9dc5f7c71db3f55e?source=copy_link";

/** How long the banner waits before it fades in, so it does not land on top of the first look at the page. */
const SHOW_DELAY_MS = 2500;

const COPY = {
  en: (brand: string) => ({ title: "🍪 Cookies", text: `We use a few cookies to see how ${brand} is used and improve it.`, accept: "Accept", refuse: "Refuse", privacy: "Privacy Policy", url: PRIVACY_URL }),
  fr: (brand: string) => ({ title: "🍪 Cookies", text: `Nous utilisons quelques cookies pour voir comment ${brand} est utilisé et l'améliorer.`, accept: "Accepter", refuse: "Refuser", privacy: "Politique de confidentialité", url: PRIVACY_URL_FR }),
};

// Each brand wears its own colors on a white card, as soft glows in two corners: ernest's indigo and lime, Kithe's violet and lavender.
const THEMES = {
  ernest: {
    card: "border-border bg-white text-[#0E1626]",
    glow: "radial-gradient(60% 90% at 12% 0%, rgba(123,111,240,0.22), transparent 65%), radial-gradient(55% 80% at 95% 100%, rgba(222,255,0,0.34), transparent 65%)",
    rule: "border-[#0E1626]/10",
    hover: "hover:bg-[#0E1626]/[0.07] focus-visible:bg-[#0E1626]/[0.07]",
    font: undefined,
    muted: "text-[#0E1626]/55 hover:text-[#0E1626]",
  },
  kithe: {
    card: "border-[#D4CEFF] bg-white text-[#1A1A2E]",
    glow: "radial-gradient(60% 90% at 12% 0%, rgba(125,112,240,0.26), transparent 65%), radial-gradient(55% 80% at 95% 100%, rgba(236,188,255,0.38), transparent 65%)",
    rule: "border-[#1A1A2E]/10",
    hover: "hover:bg-[#7D70F0]/15 focus-visible:bg-[#7D70F0]/15",
    font: { fontFamily: "'Quicksand', sans-serif" },
    muted: "text-[#1A1A2E]/55 hover:text-[#1A1A2E]",
  },
};

/**
 * Asks once for audience measurement. Accept and Refuse are the same size and style, as the CNIL requires, and nothing
 * loads before a choice. A footer link reopens it so the choice can be changed as easily as it was made.
 */
const CookieBanner = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    // Shown after mount and after a short wait, never during server rendering, so prerendered pages stay clean.
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (getConsent() === null) timer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    // Reopened from a footer link: straight away.
    const reopen = () => {
      if (timer) clearTimeout(timer);
      setOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, reopen);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(OPEN_SETTINGS_EVENT, reopen);
    };
  }, []);

  if (!open) return null;

  const kithe = isKitheDomain();
  const theme = kithe ? THEMES.kithe : THEMES.ernest;
  const lang = typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("fr") ? "fr" : "en";
  const c = COPY[lang](kithe ? "Kithe" : "ernest");
  const choose = (value: "granted" | "denied") => {
    setConsent(value);
    setOpen(false);
  };
  // The community signup has a sticky bar at the bottom on mobile: sit at the top there.
  const topOnMobile = pathname === "/community-signup";
  const buttonClass = cn("h-11 text-sm font-semibold transition-colors focus-visible:outline-none", theme.hover);

  return (
    <div
      role="dialog"
      aria-label="Cookies"
      style={{ backgroundImage: theme.glow, ...theme.font }}
      className={cn(
        "fixed z-[70] inset-x-3 overflow-hidden rounded-2xl border shadow-xl duration-500 animate-in fade-in-0 sm:inset-x-auto sm:left-4 sm:w-[340px]",
        theme.card,
        topOnMobile ? "top-3 slide-in-from-top-2 sm:bottom-4 sm:top-auto sm:slide-in-from-bottom-2" : "bottom-3 slide-in-from-bottom-2 sm:bottom-4",
      )}
    >
      <div className="px-5 pb-3 pt-4">
        <h2 className="text-lg font-bold tracking-tight" style={kithe ? { WebkitTextStroke: "0.4px currentColor" } : undefined}>{c.title}</h2>
        <p className="mt-1 text-sm">
          {c.text}{" "}
          <a href={c.url} target="_blank" rel="noopener noreferrer" className={cn("text-xs underline underline-offset-2 transition-colors", theme.muted)}>
            {c.privacy}
          </a>
        </p>
      </div>
      <div className={cn("grid grid-cols-2 border-t", theme.rule)}>
        <button type="button" className={cn(buttonClass, "border-r", theme.rule)} onClick={() => choose("denied")}>{c.refuse}</button>
        <button type="button" className={buttonClass} onClick={() => choose("granted")}>{c.accept}</button>
      </div>
    </div>
  );
};

export default CookieBanner;
